import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    output,
    signal,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TelXLLoadingComponent, TelXLSearchComponent } from '@dxp/elements';
import {
    IContactInternal,
    IContactInternalDirectoryEvent,
    IContactInternals,
    IDirectoryListing,
    IDirectoryListingEvent,
    IDirectorySelection,
} from '@dxp/core/models';
import { ContactDirectoryService } from '@dxp/core/services';
import { debounceTime, filter, fromEvent, map, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListingMode, MediaType } from '@dxp/core/types';
import { TelXLDirectoryListingItemComponent } from '../item/directory-listing-item.component';

const LIMIT = 20;

@Component({
    selector: 'telxl-shared-internal-directory-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TelXLLoadingComponent,
        TelXLDirectoryListingItemComponent,
        TelXLSearchComponent,
    ],
    templateUrl: './internal-directory-panel.component.html',
    styleUrl: './internal-directory-panel.component.scss',
})
export class TelXLInternalDirectoryPanelComponent
    implements OnInit, AfterViewInit
{
    readonly #destroyRef = inject(DestroyRef);
    readonly #contactDirectoryService = inject(ContactDirectoryService);

    @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

    customButtonTemplate = input<TemplateRef<any>>();
    selected = input<IDirectorySelection>();
    highlight = input<number>();
    height = input.required<string>();
    mode = input<ListingMode[]>([]);
    displayMediaIcons = input<MediaType[]>([]);
    disabled = input<boolean>(false);
    clicked = output<IContactInternalDirectoryEvent>();

    readonly #internalDirectory = signal<IContactInternals>({
        count: 0,
        users: [],
    });

    readonly searchText = signal<string>('');
    readonly isLoading = signal<boolean>(false);

    readonly calculateHeight = computed<string>(
        () => `calc(${this.height()} - 100px)`,
    );

    listings = computed<IDirectoryListing[]>(() =>
        this.#internalDirectory().users.map(user => {
            return {
                id: user.id,
                name: user.displayName,
                jobTitle: user.jobTitle,
                department: user.department,
                businessNumber:
                    user.businessPhones.length > 0
                        ? user.businessPhones[0]
                        : undefined,
                mobileNumber: user.mobilePhone,
                email: '',
                presence: user.presence,
                object: JSON.stringify(user),
            };
        }),
    );

    ngOnInit() {
        this.#loadContacts(true);
    }

    ngAfterViewInit() {
        if (this.scrollContainer) {
            fromEvent(this.scrollContainer.nativeElement, 'scroll')
                .pipe(
                    debounceTime(300),
                    map(() => this.scrollContainer.nativeElement),
                    filter(
                        container =>
                            container.scrollTop + container.clientHeight >=
                            container.scrollHeight - 50,
                    ),
                    tap(() => this.#loadContacts(false)),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        }
    }

    #hasMoreData(): boolean {
        return (
            this.#internalDirectory().users.length <
            this.#internalDirectory().count
        );
    }

    #loadContacts(initialLoad: boolean) {
        if ((!initialLoad && !this.#hasMoreData()) || this.isLoading()) return;

        this.isLoading.set(true);

        return this.#contactDirectoryService
            .fetchInternalContacts(
                LIMIT,
                this.#internalDirectory().users.length,
                this.searchText().length > 0 ? this.searchText() : undefined,
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(data => {
                this.#internalDirectory.update(current => {
                    return {
                        count: data.count,
                        users: [...current.users, ...data.users],
                    };
                });

                this.isLoading.set(false);
            });
    }

    selectedChanged(event: IDirectoryListingEvent) {
        this.clicked.emit({
            type: event.type,
            item: {
                ...(this.#internalDirectory().users.find(
                    c => c.id.toString() === event.item.id,
                ) as IContactInternal),
                phoneNumber: event.item.phoneNumber,
            },
        });
    }

    searchChanged(searchText: string) {
        this.#internalDirectory.set({
            count: 0,
            users: [],
        });

        this.searchText.set(searchText);
        this.#loadContacts(true);
    }
}
